import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Package, ShoppingBag, DollarSign, TrendingUp, Loader2, Download, Trash2, Edit, Copy } from "lucide-react";

const CATS = ["earbuds","headphones","speakers","smartwatches","chargers","power-banks","phone-accessories","gaming-gear","keyboards","mice","cameras","smart-home"];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  React.useEffect(() => { if (!loading && (!user || user.role !== "admin")) nav("/login", { state: { from: "/admin" }}); }, [user, loading, nav]);
  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <p className="chip mb-2">Admin</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Dashboard</h1>
      </div>
      <Tabs defaultValue="overview">
        <TabsList className="bg-card border border-white/5 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="admin-tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="qa" data-testid="admin-tab-qa">Q&amp;A</TabsTrigger>
          <TabsTrigger value="bundles" data-testid="admin-tab-bundles">Bundles</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast">Broadcast</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="pt-6"><Overview /></TabsContent>
        <TabsContent value="products" className="pt-6"><Products /></TabsContent>
        <TabsContent value="suppliers" className="pt-6"><Suppliers /></TabsContent>
        <TabsContent value="orders" className="pt-6"><Orders /></TabsContent>
        <TabsContent value="coupons" className="pt-6"><Coupons /></TabsContent>
        <TabsContent value="reviews" className="pt-6"><Reviews /></TabsContent>
        <TabsContent value="qa" className="pt-6"><QAAdmin /></TabsContent>
        <TabsContent value="bundles" className="pt-6"><BundlesTab /></TabsContent>
        <TabsContent value="broadcast" className="pt-6"><BroadcastTab /></TabsContent>
        <TabsContent value="settings" className="pt-6"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function Overview() {
  const { data: stats } = useQuery({ queryKey: ["adminStats"], queryFn: adminApi.stats });
  const [days, setDays] = useState(30);
  const { data: analytics } = useQuery({ queryKey: ["analytics", days], queryFn: () => adminApi.analytics(days) });
  const s = stats || {};
  const a = analytics || {};
  const cards = [
    { label: `Revenue (${days}d)`, value: `$${(a.revenue || 0).toFixed(2)}`, icon: DollarSign },
    { label: `Orders (${days}d)`, value: a.orders || 0, icon: ShoppingBag },
    { label: "Avg. order value", value: `$${(a.aov || 0).toFixed(2)}`, icon: TrendingUp },
    { label: "Active carts", value: a.active_carts || 0, icon: Package },
  ];
  const maxRev = Math.max(1, ...(a.daily || []).map((d) => d.revenue));
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Store performance</p>
        <div className="flex gap-1">
          {[7, 30, 90].map((n) => (
            <button key={n} onClick={() => setDays(n)} className={`text-xs px-3 py-1 rounded-full border ${days === n ? "border-primary text-primary bg-primary/10" : "border-white/10 text-muted-foreground hover:text-white"}`} data-testid={`analytics-range-${n}`}>Last {n}d</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-card border border-white/5 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase tracking-widest">{c.label}</span>
              <c.icon className="h-4 w-4 text-primary" />
            </div>
            <p className="font-display text-2xl md:text-3xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Revenue chart */}
        <div className="rounded-2xl bg-card border border-white/5 p-5" data-testid="analytics-chart">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-sm">Revenue trend</h3>
            <span className="text-xs text-muted-foreground">{a.items_sold || 0} items sold</span>
          </div>
          {(a.daily || []).length === 0 ? (
            <p className="text-xs text-muted-foreground py-8 text-center">No sales in this window yet — launch your first ad and this will fill up.</p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {(a.daily || []).map((d) => (
                <div key={d.date} className="flex-1 min-w-[6px] flex flex-col items-center group relative">
                  <div className="w-full bg-primary/70 hover:bg-primary rounded-t transition-all" style={{ height: `${(d.revenue / maxRev) * 100}%` }} title={`${d.date}: $${d.revenue.toFixed(2)}`}></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top products */}
        <div className="rounded-2xl bg-card border border-white/5 p-5" data-testid="analytics-top-products">
          <h3 className="font-semibold text-sm mb-3">Top products by revenue</h3>
          {(a.top_products || []).length === 0 ? (
            <p className="text-xs text-muted-foreground py-8 text-center">No sales yet — top sellers will show here.</p>
          ) : (
            <ol className="space-y-2">
              {a.top_products.map((p, i) => (
                <li key={p.product_id} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="flex-1 truncate">{p.title || p.product_id}</span>
                  <span className="text-muted-foreground text-xs shrink-0">{p.sold} sold</span>
                  <span className="text-primary font-medium shrink-0 w-16 text-right">${p.revenue.toFixed(2)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Funnel + coupon perf */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-2xl bg-card border border-white/5 p-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending orders</p>
          <p className="text-xl font-bold mt-1">{a.pending_orders || 0}</p>
        </div>
        <div className="rounded-2xl bg-card border border-white/5 p-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Active carts</p>
          <p className="text-xl font-bold mt-1">{a.active_carts || 0}</p>
        </div>
        <div className="rounded-2xl bg-card border border-white/5 p-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Abandoned emails sent</p>
          <p className="text-xl font-bold mt-1">{a.abandoned_recovered_pipeline || 0}</p>
        </div>
        <div className="rounded-2xl bg-card border border-white/5 p-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total products</p>
          <p className="text-xl font-bold mt-1">{s.published || 0}<span className="text-sm text-muted-foreground">/{s.products || 0}</span></p>
        </div>
      </div>

      {(a.coupons || []).length > 0 && (
        <div className="rounded-2xl bg-card border border-white/5 p-5" data-testid="analytics-coupons">
          <h3 className="font-semibold text-sm mb-3">Coupon performance</h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            {a.coupons.map((c) => (
              <div key={c.code} className="rounded-lg bg-secondary/40 p-3">
                <p className="font-mono text-primary text-xs">{c.code}</p>
                <p className="text-lg font-bold mt-1">${c.revenue.toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground">{c.orders} order{c.orders === 1 ? "" : "s"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Products() {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({ queryKey: ["adminProducts"], queryFn: adminApi.products });
  const [edit, setEdit] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const openNew = () => setEdit({ title: "", description: "", category: "earbuds", brand: "", price: 0, sku: "", inventory: 0, images: [], variants: [], specs: {}, tags: [], published: true, featured: false });
  const del = async (id) => { if (!confirm("Delete product?")) return; await adminApi.deleteProduct(id); qc.invalidateQueries({ queryKey: ["adminProducts"] }); toast.success("Deleted"); };
  const duplicate = async (id) => {
    try {
      const dup = await adminApi.duplicateProduct(id);
      toast.success("Duplicated as draft — edit and publish when ready");
      await qc.invalidateQueries({ queryKey: ["adminProducts"] });
      setEdit(dup);  // open the editor on the new copy immediately
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };

  const filtered = products.filter((p) => {
    if (statusFilter === "live" && !p.published) return false;
    if (statusFilter === "draft" && p.published) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (p.title || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q);
  });

  const allSelectedOnPage = filtered.length > 0 && filtered.every((p) => selected.has(p.product_id));
  const someSelectedOnPage = filtered.some((p) => selected.has(p.product_id)) && !allSelectedOnPage;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) filtered.forEach((p) => next.delete(p.product_id));
      else filtered.forEach((p) => next.add(p.product_id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`Permanently delete ${ids.length} product${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await adminApi.bulkDeleteProducts(ids);
      toast.success(`Deleted ${r.deleted}`);
      clearSelection();
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
    setBusy(false);
  };

  const bulkStatus = async (published) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await adminApi.bulkUpdateProducts({ product_ids: ids, published });
      toast.success(`${published ? "Published" : "Unpublished"} ${ids.length}`);
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk update failed"); }
    setBusy(false);
  };

  const seedReviews = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`Add 3 realistic sample reviews to ${ids.length} product${ids.length === 1 ? "" : "s"}? Only do this on launch to seed social proof — do not use on already-selling products.`)) return;
    setBusy(true);
    try {
      const r = await adminApi.seedReviews(ids, 3);
      toast.success(`Added ${r.seeded} reviews`);
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    setBusy(false);
  };

  const applyBulkEdit = async (patch) => {
    setBusy(true);
    try {
      const r = await adminApi.bulkUpdateProducts({ product_ids: Array.from(selected), ...patch });
      toast.success(`Updated ${r.updated}`);
      setBulkEditOpen(false);
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk edit failed"); }
    setBusy(false);
  };

  const selectedCount = selected.size;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-muted-foreground text-sm">{products.length} products{filter || statusFilter !== "all" ? ` · ${filtered.length} shown` : ""}</p>
        <div className="flex gap-2 items-center">
          <Input placeholder="Search title / brand / SKU…" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-56" data-testid="products-search" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="live">Live only</SelectItem>
              <SelectItem value="draft">Drafts only</SelectItem>
            </SelectContent>
          </Select>
          <Button className="btn-primary" onClick={openNew} data-testid="admin-new-product">New product</Button>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="sticky top-2 z-10 rounded-2xl bg-primary/10 border border-primary/40 backdrop-blur px-4 py-3 mb-3 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-primary">{selectedCount} selected</span>
          <span className="text-xs text-muted-foreground">·</span>
          <Button size="sm" variant="outline" className="border-white/15" onClick={() => bulkStatus(true)} disabled={busy} data-testid="bulk-publish">Publish</Button>
          <Button size="sm" variant="outline" className="border-white/15" onClick={() => bulkStatus(false)} disabled={busy} data-testid="bulk-unpublish">Unpublish</Button>
          <Button size="sm" variant="outline" className="border-white/15" onClick={() => setBulkEditOpen(true)} disabled={busy} data-testid="bulk-edit">Edit…</Button>
          <Button size="sm" variant="outline" className="border-white/15" onClick={seedReviews} disabled={busy} data-testid="bulk-seed-reviews">Seed reviews</Button>
          <Button size="sm" variant="destructive" onClick={bulkDelete} disabled={busy} data-testid="bulk-delete">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />} Delete
          </Button>
          <button onClick={clearSelection} className="ml-auto text-xs text-muted-foreground hover:text-white" data-testid="bulk-clear">Clear</button>
        </div>
      )}

      <div className="rounded-2xl bg-card border border-white/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  ref={(el) => { if (el) el.indeterminate = someSelectedOnPage; }}
                  onChange={toggleAll}
                  className="accent-primary h-4 w-4"
                  data-testid="select-all"
                />
              </th>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Stock</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const isSel = selected.has(p.product_id);
              return (
                <tr key={p.product_id} className={`border-t border-white/5 ${isSel ? "bg-primary/5" : ""}`} data-testid={`admin-product-${p.product_id}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleOne(p.product_id)}
                      className="accent-primary h-4 w-4"
                      data-testid={`select-${p.product_id}`}
                    />
                  </td>
                  <td className="px-4 py-3 flex items-center gap-3"><img src={p.images?.[0]} className="h-10 w-10 rounded object-cover" alt="" /><span className="line-clamp-1">{p.title}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{p.category}</td>
                  <td className="px-4 py-3 text-right">${p.price?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{p.inventory}</td>
                  <td className="px-4 py-3 text-center"><span className={`chip ${p.published ? "text-primary border-primary/30" : ""}`}>{p.published ? "Live" : "Draft"}</span></td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => setEdit(p)} data-testid={`edit-${p.product_id}`}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => duplicate(p.product_id)} title="Duplicate" data-testid={`dup-${p.product_id}`}><Copy className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(p.product_id)} data-testid={`del-${p.product_id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No products match your filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {edit && <ProductEditor product={edit} onClose={() => setEdit(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ["adminProducts"] }); setEdit(null); }} />}
      {bulkEditOpen && <BulkEditDialog count={selectedCount} onClose={() => setBulkEditOpen(false)} onApply={applyBulkEdit} />}
    </div>
  );
}

function BulkEditDialog({ count, onClose, onApply }) {
  const [category, setCategory] = useState("");
  const [markup, setMarkup] = useState("");
  const [setPrice, setSetPrice] = useState("");
  const [setInv, setSetInv] = useState("");
  const [invDelta, setInvDelta] = useState("");
  const [addTags, setAddTags] = useState("");
  const apply = () => {
    const patch = {};
    if (category) patch.category = category;
    if (markup !== "") patch.markup_percent = parseFloat(markup);
    if (setPrice !== "") patch.set_price = parseFloat(setPrice);
    if (setInv !== "") patch.set_inventory = parseInt(setInv);
    if (invDelta !== "") patch.inventory_delta = parseInt(invDelta);
    if (addTags.trim()) patch.add_tags = addTags.split(",").map((s) => s.trim()).filter(Boolean);
    if (Object.keys(patch).length === 0) { toast.error("Change at least one field"); return; }
    onApply(patch);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk edit {count} product{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">Leave a field blank to skip it. Applies to every selected product.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Label>Move to category</Label>
            <Select value={category || "__none__"} onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Leave unchanged" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Leave unchanged</SelectItem>
                {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Set price to</Label><Input type="number" step="0.01" placeholder="e.g. 29.99" value={setPrice} onChange={(e) => setSetPrice(e.target.value)} data-testid="bulk-set-price" /></div>
          <div><Label>OR markup %</Label><Input type="number" step="1" placeholder="e.g. 20 (raises by 20%)" value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="bulk-markup" /></div>
          <div><Label>Set inventory to</Label><Input type="number" step="1" placeholder="e.g. 999 (fixes 0-stock)" value={setInv} onChange={(e) => setSetInv(e.target.value)} data-testid="bulk-set-inv" /></div>
          <div><Label>OR inventory delta</Label><Input type="number" step="1" placeholder="e.g. 50 or -10" value={invDelta} onChange={(e) => setInvDelta(e.target.value)} data-testid="bulk-inv" /></div>
          <div className="col-span-2"><Label>Add tags (comma-sep)</Label><Input placeholder="sale, hot" value={addTags} onChange={(e) => setAddTags(e.target.value)} data-testid="bulk-tags" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="btn-primary" onClick={apply} data-testid="bulk-edit-apply">Apply to {count}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [p, setP] = useState({ ...product, images: (product.images || []).join("\n"), tags: (product.tags || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...p, images: p.images.split("\n").map(s => s.trim()).filter(Boolean),
        tags: p.tags.split(",").map(s => s.trim()).filter(Boolean),
        price: parseFloat(p.price) || 0, inventory: parseInt(p.inventory) || 0,
        compare_at_price: p.compare_at_price ? parseFloat(p.compare_at_price) : null };
      await adminApi.saveProduct(product.product_id, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="product-editor">
        <DialogHeader><DialogTitle>{product.product_id ? "Edit product" : "New product"}</DialogTitle><DialogDescription className="text-muted-foreground text-sm">Update details, images, and inventory. Uncheck Published to save as draft.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2"><Label>Title</Label><Input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} data-testid="pe-title" /></div>
          <div><Label>Category</Label>
            <Select value={p.category} onValueChange={(v) => setP({ ...p, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Brand</Label><Input value={p.brand} onChange={(e) => setP({ ...p, brand: e.target.value })} /></div>
          <div><Label>Price</Label><Input type="number" step="0.01" value={p.price} onChange={(e) => setP({ ...p, price: e.target.value })} data-testid="pe-price" /></div>
          <div><Label>Compare at price</Label><Input type="number" step="0.01" value={p.compare_at_price || ""} onChange={(e) => setP({ ...p, compare_at_price: e.target.value })} /></div>
          <div><Label>SKU</Label><Input value={p.sku} onChange={(e) => setP({ ...p, sku: e.target.value })} /></div>
          <div><Label>Inventory</Label><Input type="number" value={p.inventory} onChange={(e) => setP({ ...p, inventory: e.target.value })} /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={4} value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} /></div>
          <div className="col-span-2"><Label>Images (one URL per line)</Label><Textarea rows={3} value={p.images} onChange={(e) => setP({ ...p, images: e.target.value })} /></div>
          <div className="col-span-2"><Label>Hero video URL (optional — YouTube, Vimeo, or direct .mp4)</Label><Input placeholder="https://www.youtube.com/watch?v=… or https://cdn.you/promo.mp4" value={p.video_url || ""} onChange={(e) => setP({ ...p, video_url: e.target.value })} data-testid="pe-video-url" /></div>
          <div className="col-span-2"><Label>Tags (comma separated)</Label><Input value={p.tags} onChange={(e) => setP({ ...p, tags: e.target.value })} /></div>
          <div className="col-span-2"><Label>Shipping info</Label><Input value={p.shipping_info || ""} onChange={(e) => setP({ ...p, shipping_info: e.target.value })} /></div>
          <div className="flex items-center gap-2"><Switch checked={p.published} onCheckedChange={(v) => setP({ ...p, published: v })} data-testid="pe-published" /><Label>Published</Label></div>
          <div className="flex items-center gap-2"><Switch checked={p.featured} onCheckedChange={(v) => setP({ ...p, featured: v })} /><Label>Featured</Label></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="border-white/15">Cancel</Button>
          <Button className="btn-primary" onClick={save} disabled={saving} data-testid="pe-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Suppliers() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers"], queryFn: adminApi.suppliers });
  const [active, setActive] = useState("aliexpress");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const { data: catalogRes, isLoading } = useQuery({ queryKey: ["catalog", active, search, category], queryFn: () => adminApi.supplierCatalog(active, { search: search || undefined, category: category || undefined }), enabled: !!active });
  // Backend now returns {items, live, using_mock_fallback, error} — keep back-compat if it ever returns an array.
  const catalog = Array.isArray(catalogRes) ? catalogRes : (catalogRes?.items || []);
  const catalogError = Array.isArray(catalogRes) ? null : catalogRes?.error;
  const usingMockFallback = Array.isArray(catalogRes) ? false : !!catalogRes?.using_mock_fallback;
  const [importing, setImporting] = useState(null);
  const [bulking, setBulking] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const doImport = async (item, useAI) => {
    setImporting(item.ext_id);
    try {
      const prod = await adminApi.importProduct({ supplier: active, ext_id: item.ext_id, use_ai_enrichment: useAI });
      toast.success(`Imported "${prod.title}" as draft`);
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
      qc.invalidateQueries({ queryKey: ["catalog", active, search, category] });
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setImporting(null);
  };

  const bulkImportAll = async (publish) => {
    const pending = catalog.filter((i) => !i.imported);
    if (pending.length === 0) { toast.info("Everything shown is already imported"); return; }
    if (!confirm(`Import ${pending.length} product${pending.length === 1 ? "" : "s"} from ${active}${publish ? " and publish immediately" : " as drafts"}? AI enrichment will run (may take ~2s per item).`)) return;
    setBulking(true);
    try {
      const r = await adminApi.bulkImport({
        supplier: active,
        ext_ids: pending.map((i) => i.ext_id),
        use_ai_enrichment: true,
        publish,
      });
      toast.success(`Imported ${r.imported} · skipped ${r.skipped}${r.failed ? ` · failed ${r.failed}` : ""}`);
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
      qc.invalidateQueries({ queryKey: ["catalog", active, search, category] });
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk import failed"); }
    setBulking(false);
  };

  const runDiagnose = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const r = await adminApi.supplierDiagnose(active);
      setDiag(r);
    } catch (e) {
      setDiag({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setDiagLoading(false);
  };

  const [urlInput, setUrlInput] = useState("");
  const [urlPublish, setUrlPublish] = useState(false);
  const [urlBusy, setUrlBusy] = useState(false);
  const doImportUrl = async () => {
    const supportsUrl = active === "cjdropshipping" || active === "aliexpress";
    if (!supportsUrl) return;
    const label = active === "aliexpress" ? "AliExpress" : "CJ";
    if (!urlInput.trim()) { toast.error(`Paste a ${label} product URL first`); return; }
    setUrlBusy(true);
    try {
      const fn = active === "aliexpress" ? adminApi.aliexpressImportByUrl : adminApi.cjImportByUrl;
      const prod = await fn({ url: urlInput.trim(), use_ai_enrichment: true, publish: urlPublish });
      toast.success(`Imported "${prod.title}"${urlPublish ? " and published" : " as draft"}`);
      setUrlInput("");
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
      qc.invalidateQueries({ queryKey: ["catalog", active, search, category] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setUrlBusy(false);
  };

  const pendingCount = catalog.filter((i) => !i.imported).length;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="supplier-picker">
        {suppliers.map((s) => (
          <button key={s.key} onClick={() => setActive(s.key)} className={`px-4 py-2 rounded-lg text-sm border flex items-center gap-2 ${active === s.key ? "border-primary bg-primary/10 text-primary" : "border-white/10 hover:border-white/20"}`} data-testid={`supplier-${s.key}`}>
            {s.name}
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.live ? "bg-primary/20 text-primary" : "bg-white/5 text-muted-foreground"}`}>{s.live ? "LIVE" : "MOCK"}</span>
          </button>
        ))}
      </div>
      {(active === "cjdropshipping" || active === "aliexpress") && (
        <div className="rounded-2xl bg-card border border-primary/30 p-4 mb-6" data-testid={`${active}-url-import`}>
          <div className="flex items-center gap-2 mb-2">
            <Download className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">
              {active === "aliexpress"
                ? "Import a specific AliExpress product by URL"
                : "Import a specific CJ product by URL"}
            </h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {active === "aliexpress" ? (
              <>Browse <a href="https://www.aliexpress.com/category/509/consumer-electronics.html" target="_blank" rel="noreferrer" className="underline text-primary">AliExpress consumer electronics</a>, copy the product URL (from address bar or "Share"), and paste it below. AI polishes the title + description automatically. No API key needed.</>
            ) : (
              <>Browse <a href="https://cjdropshipping.com/list/wholesale-consumer-electronics-l-38A1B9E5-4B4C-46E7-A47A-9F80E3B4D3D0.html" target="_blank" rel="noreferrer" className="underline text-primary">CJ's electronics catalog</a>, find the exact product you want, copy its URL, and paste it below. AI will polish the title + description automatically.</>
            )}
          </p>
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              placeholder={active === "aliexpress"
                ? "https://www.aliexpress.com/item/1005006123456789.html"
                : "https://cjdropshipping.com/product/xxx-p-1234567890.html"}
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="flex-1"
              data-testid={`${active}-url-input`}
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground shrink-0 px-2">
              <input type="checkbox" checked={urlPublish} onChange={(e) => setUrlPublish(e.target.checked)} data-testid={`${active}-url-publish`} />
              Publish immediately
            </label>
            <Button className="btn-primary shrink-0" onClick={doImportUrl} disabled={urlBusy} data-testid={`${active}-url-import-btn`}>
              {urlBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : "Import"}
            </Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <Input placeholder="Search supplier catalog…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="supplier-search" />
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" className="border-white/15" onClick={runDiagnose} disabled={diagLoading} data-testid="supplier-diagnose">
          {diagLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing…</> : "Test API"}
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" className="border-white/15" onClick={() => bulkImportAll(false)} disabled={bulking || pendingCount === 0} data-testid="bulk-import-drafts">
            {bulking ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : `Import ${pendingCount} as drafts`}
          </Button>
          <Button className="btn-primary" onClick={() => bulkImportAll(true)} disabled={bulking || pendingCount === 0} data-testid="bulk-import-publish">
            {bulking ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : `Import & publish all (${pendingCount})`}
          </Button>
        </div>
      </div>
      {usingMockFallback && catalogError && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-200 text-xs p-3 mb-4" data-testid="supplier-error-banner">
          <div className="font-medium mb-1">⚠️ Live API failed — showing mock catalog</div>
          <div className="opacity-90">{catalogError.message || "Unknown error"}{catalogError.status ? ` (HTTP ${catalogError.status})` : ""}</div>
          {catalogError.raw && (
            <pre className="mt-2 max-h-40 overflow-auto text-[10px] opacity-80">{typeof catalogError.raw === "string" ? catalogError.raw : JSON.stringify(catalogError.raw, null, 2)}</pre>
          )}
        </div>
      )}
      {diag && (
        <div className={`rounded-xl border text-xs p-3 mb-4 ${diag.ok ? "border-primary/40 bg-primary/10 text-primary" : "border-red-500/40 bg-red-500/10 text-red-200"}`} data-testid="supplier-diagnose-result">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium">{diag.ok ? "✅ Live API OK" : "❌ Live API failed"} — mode: {diag.mode || "unknown"}</span>
            <button onClick={() => setDiag(null)} className="opacity-60 hover:opacity-100">×</button>
          </div>
          {diag.ok && <div>Fetched {diag.item_count} items. Sample title: <span className="font-mono">{diag.sample?.[0]?.title || "—"}</span></div>}
          {!diag.ok && <div>{diag.error}{diag.status ? ` (HTTP ${diag.status})` : ""}</div>}
          {diag.raw && (
            <pre className="mt-2 max-h-52 overflow-auto text-[10px] opacity-80">{typeof diag.raw === "string" ? diag.raw : JSON.stringify(diag.raw, null, 2)}</pre>
          )}
        </div>
      )}
      {isLoading && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {catalog.map((it) => (
          <div key={it.ext_id} className={`rounded-2xl bg-card border p-4 flex gap-3 ${it.imported ? "border-primary/40 opacity-70" : "border-white/5"}`} data-testid={`catalog-item-${it.ext_id}`}>
            <img src={it.image} alt="" className="h-24 w-24 rounded-lg object-cover" />
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-start gap-2">
                <p className="text-sm font-medium line-clamp-2 flex-1">{it.title}</p>
                {it.imported && <span className="chip text-primary border-primary/40 shrink-0">Imported</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{it.brand} · {it.category}</p>
              <div className="text-xs mt-1"><span className="text-muted-foreground">Cost </span>${it.cost} <span className="text-muted-foreground ml-2">MSRP </span>${it.suggested_price}</div>
              <div className="text-xs text-muted-foreground">Stock {it.stock} · ships {it.shipping_days}d</div>
              <div className="flex gap-2 mt-auto pt-2">
                {it.imported ? (
                  <Button size="sm" variant="outline" className="border-white/15" onClick={() => nav("/admin")} data-testid={`view-imported-${it.ext_id}`}>Already imported</Button>
                ) : (
                  <>
                    <Button size="sm" className="btn-primary" onClick={() => doImport(it, true)} disabled={importing === it.ext_id} data-testid={`import-ai-${it.ext_id}`}>
                      {importing === it.ext_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Download className="h-3 w-3 mr-1" /> AI import</>}
                    </Button>
                    <Button size="sm" variant="outline" className="border-white/15" onClick={() => doImport(it, false)} disabled={importing === it.ext_id} data-testid={`import-raw-${it.ext_id}`}>Raw</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({ queryKey: ["adminOrders"], queryFn: adminApi.orders });
  const setStatus = async (id, s) => { await adminApi.fulfill(id, s); qc.invalidateQueries({ queryKey: ["adminOrders"] }); toast.success("Updated"); };
  return (
    <div className="rounded-2xl bg-card border border-white/5 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-3">Order</th><th className="text-left px-4 py-3">Customer</th><th className="text-right px-4 py-3">Total</th><th className="text-center px-4 py-3">Payment</th><th className="text-center px-4 py-3">Tracking</th><th className="text-right px-4 py-3">Actions</th></tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.order_id} className="border-t border-white/5" data-testid={`admin-order-${o.order_id}`}>
              <td className="px-4 py-3 font-mono">{o.order_id.slice(0, 12)}</td>
              <td className="px-4 py-3">{o.customer_email || "guest"}</td>
              <td className="px-4 py-3 text-right">${o.totals?.total?.toFixed(2)}</td>
              <td className="px-4 py-3 text-center"><span className="chip">{o.payment_status}</span></td>
              <td className="px-4 py-3 text-center">
                <Select value={o.tracking_status} onValueChange={(v) => setStatus(o.order_id, v)}>
                  <SelectTrigger className="w-40 h-8 mx-auto text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["awaiting_payment","processing","shipped","out_for_delivery","delivered","cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-4 py-3 text-right text-xs text-muted-foreground">{o.tracking_number}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Coupons() {
  const qc = useQueryClient();
  const { data: coupons = [] } = useQuery({ queryKey: ["adminCoupons"], queryFn: adminApi.coupons });
  const [c, setC] = useState({ code: "", percent_off: 10, active: true });
  const add = async () => { await adminApi.saveCoupon({ ...c, code: c.code.toUpperCase() }); qc.invalidateQueries({ queryKey: ["adminCoupons"] }); setC({ code: "", percent_off: 10, active: true }); toast.success("Saved"); };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-2xl bg-card border border-white/5 p-6">
        <h3 className="font-semibold mb-4">Create coupon</h3>
        <div className="space-y-3">
          <Input placeholder="CODE" value={c.code} onChange={(e) => setC({ ...c, code: e.target.value })} data-testid="coupon-code-input" />
          <div><Label>Percent off</Label><Input type="number" value={c.percent_off} onChange={(e) => setC({ ...c, percent_off: parseFloat(e.target.value) })} /></div>
          <div className="flex items-center gap-2"><Switch checked={c.active} onCheckedChange={(v) => setC({ ...c, active: v })} /><Label>Active</Label></div>
          <Button className="btn-primary" onClick={add} data-testid="coupon-save">Save coupon</Button>
        </div>
      </div>
      <div className="rounded-2xl bg-card border border-white/5 p-6">
        <h3 className="font-semibold mb-4">Active coupons</h3>
        <ul className="space-y-2">{coupons.map((c) => (
          <li key={c.code} className="flex justify-between text-sm border-b border-white/5 pb-2">
            <span className="font-mono">{c.code}</span><span>{c.percent_off}% off · {c.active ? "active" : "inactive"}</span>
          </li>
        ))}</ul>
      </div>
    </div>
  );
}

function Reviews() {
  const qc = useQueryClient();
  const { data: reviews = [] } = useQuery({ queryKey: ["adminReviews"], queryFn: adminApi.reviews });
  const toggle = async (id) => { await adminApi.toggleReview(id); qc.invalidateQueries({ queryKey: ["adminReviews"] }); toast.success("Updated"); };
  return (
    <div className="rounded-2xl bg-card border border-white/5 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-3">Review</th><th className="text-left px-4 py-3">Author</th><th className="text-center px-4 py-3">Rating</th><th className="text-center px-4 py-3">Verified</th><th className="text-center px-4 py-3">Status</th><th className="text-right px-4 py-3">Action</th></tr>
        </thead>
        <tbody>
          {reviews.map((r) => (
            <tr key={r.review_id} className="border-t border-white/5" data-testid={`admin-review-${r.review_id}`}>
              <td className="px-4 py-3 max-w-md"><p className="font-medium">{r.title || "—"}</p><p className="text-xs text-muted-foreground line-clamp-2">{r.body}</p></td>
              <td className="px-4 py-3">{r.user_name}</td>
              <td className="px-4 py-3 text-center">{"★".repeat(r.rating)}</td>
              <td className="px-4 py-3 text-center">{r.verified ? "✓" : "—"}</td>
              <td className="px-4 py-3 text-center"><span className={`chip ${r.approved ? "text-primary border-primary/30" : ""}`}>{r.approved ? "Live" : "Hidden"}</span></td>
              <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" className="border-white/15" onClick={() => toggle(r.review_id)} data-testid={`toggle-review-${r.review_id}`}>{r.approved ? "Hide" : "Show"}</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {reviews.length === 0 && <p className="text-center text-muted-foreground py-8">No reviews yet</p>}
    </div>
  );
}


function QAAdmin() {
  const qc = useQueryClient();
  const { data: questions = [] } = useQuery({ queryKey: ["adminQuestions"], queryFn: adminApi.questions });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["adminQuestions"] });
  const toggleHidden = async (id) => { await adminApi.toggleQuestionHidden(id); invalidate(); toast.success("Updated"); };
  const togglePin = async (id) => { await adminApi.toggleQuestionPin(id); invalidate(); toast.success("Updated"); };
  return (
    <div className="rounded-2xl bg-card border border-white/5 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left px-4 py-3">Question</th>
            <th className="text-left px-4 py-3">Author</th>
            <th className="text-center px-4 py-3">Votes</th>
            <th className="text-center px-4 py-3">Status</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {questions.map((q) => (
            <tr key={q.question_id} className="border-t border-white/5" data-testid={`admin-question-${q.question_id}`}>
              <td className="px-4 py-3 max-w-md"><p className="line-clamp-2">{q.text}</p></td>
              <td className="px-4 py-3">{q.user_name}</td>
              <td className="px-4 py-3 text-center">{q.upvotes || 0}</td>
              <td className="px-4 py-3 text-center">
                <span className="chip">{q.hidden ? "Hidden" : "Live"}</span>
                {q.pinned && <span className="chip text-primary border-primary/30 ml-1">Pinned</span>}
              </td>
              <td className="px-4 py-3 text-right space-x-1">
                <Button size="sm" variant="outline" className="border-white/15" onClick={() => togglePin(q.question_id)} data-testid={`pin-q-${q.question_id}`}>{q.pinned ? "Unpin" : "Pin"}</Button>
                <Button size="sm" variant="outline" className="border-white/15" onClick={() => toggleHidden(q.question_id)} data-testid={`hide-q-${q.question_id}`}>{q.hidden ? "Show" : "Hide"}</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {questions.length === 0 && <p className="text-center text-muted-foreground py-8">No questions yet</p>}
    </div>
  );
}

function BundlesTab() {
  const qc = useQueryClient();
  const { data: bundles = [] } = useQuery({ queryKey: ["adminBundles"], queryFn: adminApi.bundles });
  const { data: products = [] } = useQuery({ queryKey: ["adminProducts"], queryFn: adminApi.products });
  const [edit, setEdit] = useState(null);
  const openNew = () => setEdit({ title: "", kind: "custom", product_ids: [], bundle_price: 0, hero_image: "", active: true, discount_percent: 0, min_qty: 2 });
  const del = async (id) => { if (!confirm("Delete bundle?")) return; await adminApi.deleteBundle(id); qc.invalidateQueries({ queryKey: ["adminBundles"] }); toast.success("Deleted"); };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <p className="text-muted-foreground text-sm">{bundles.length} bundles</p>
        <Button className="btn-primary" onClick={openNew} data-testid="admin-new-bundle">New bundle</Button>
      </div>
      <div className="rounded-2xl bg-card border border-white/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Bundle</th><th className="text-center px-4 py-3">Items</th><th className="text-right px-4 py-3">Price</th><th className="text-center px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {bundles.map((b) => (
              <tr key={b.bundle_id} className="border-t border-white/5" data-testid={`admin-bundle-${b.bundle_id}`}>
                <td className="px-4 py-3"><p className="font-medium">{b.title}</p><p className="text-xs text-muted-foreground">{b.kind}</p></td>
                <td className="px-4 py-3 text-center">{b.product_ids?.length || 0}</td>
                <td className="px-4 py-3 text-right">{b.bundle_price ? `$${b.bundle_price.toFixed(2)}` : `${b.discount_percent}% off`}</td>
                <td className="px-4 py-3 text-center"><span className={`chip ${b.active !== false ? "text-primary border-primary/30" : ""}`}>{b.active !== false ? "Live" : "Off"}</span></td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button variant="ghost" size="icon" onClick={() => setEdit({ ...b, hero_image: b.hero_image || "" })} data-testid={`edit-bundle-${b.bundle_id}`}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(b.bundle_id)} data-testid={`del-bundle-${b.bundle_id}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bundles.length === 0 && <p className="text-center text-muted-foreground py-8">No bundles yet. Create one to start upselling.</p>}
      </div>
      {edit && <BundleEditor bundle={edit} products={products} onClose={() => setEdit(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ["adminBundles"] }); setEdit(null); }} />}
    </div>
  );
}

function BundleEditor({ bundle, products, onClose, onSaved }) {
  const [b, setB] = useState({ ...bundle });
  const [saving, setSaving] = useState(false);
  const selected = products.filter((p) => (b.product_ids || []).includes(p.product_id));
  const msrp = selected.reduce((s, p) => s + p.price, 0);
  const toggle = (pid) => {
    const set = new Set(b.product_ids || []);
    if (set.has(pid)) set.delete(pid); else set.add(pid);
    setB({ ...b, product_ids: Array.from(set) });
  };
  const save = async () => {
    if ((b.product_ids || []).length < 2) { toast.error("Pick at least 2 products"); return; }
    setSaving(true);
    try {
      const payload = { ...b, bundle_price: b.bundle_price ? parseFloat(b.bundle_price) : null,
        discount_percent: parseFloat(b.discount_percent) || 0, min_qty: parseInt(b.min_qty) || 2 };
      await adminApi.saveBundle(bundle.bundle_id, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bundle-editor">
        <DialogHeader><DialogTitle>{bundle.bundle_id ? "Edit bundle" : "New bundle"}</DialogTitle><DialogDescription className="text-muted-foreground text-sm">Pick 2+ products and set a bundle price or discount %.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Title</Label><Input value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} data-testid="bundle-title-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Kind</Label>
              <Select value={b.kind} onValueChange={(v) => setB({ ...b, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom price</SelectItem>
                  <SelectItem value="bundle_with">Bundle discount</SelectItem>
                  <SelectItem value="quantity_discount">Quantity discount</SelectItem>
                  <SelectItem value="global">Global (all products)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Bundle price</Label><Input type="number" step="0.01" placeholder={`MSRP $${msrp.toFixed(2)}`} value={b.bundle_price ?? ""} onChange={(e) => setB({ ...b, bundle_price: e.target.value })} data-testid="bundle-price-input" /></div>
            <div><Label>Discount %</Label><Input type="number" value={b.discount_percent} onChange={(e) => setB({ ...b, discount_percent: e.target.value })} /></div>
          </div>
          <div><Label>Hero image URL (optional)</Label><Input value={b.hero_image} onChange={(e) => setB({ ...b, hero_image: e.target.value })} /></div>
          <div className="flex items-center gap-2"><Switch checked={b.active !== false} onCheckedChange={(v) => setB({ ...b, active: v })} data-testid="bundle-active" /><Label>Active</Label></div>
          <div>
            <Label>Products in this bundle ({(b.product_ids || []).length})</Label>
            <div className="mt-2 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-2">
              {products.map((p) => {
                const on = (b.product_ids || []).includes(p.product_id);
                return (
                  <button key={p.product_id} type="button" onClick={() => toggle(p.product_id)} className={`flex items-center gap-2 rounded-lg border p-2 text-left ${on ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/20"}`} data-testid={`bundle-pick-${p.product_id}`}>
                    <img src={p.images?.[0]} className="h-10 w-10 rounded object-cover" alt="" />
                    <div className="flex-1 min-w-0"><p className="text-xs line-clamp-1">{p.title}</p><p className="text-[10px] text-muted-foreground">${p.price?.toFixed(2)}</p></div>
                  </button>
                );
              })}
            </div>
          </div>
          {selected.length > 0 && (
            <div className="rounded-xl bg-black/30 p-3 text-xs text-muted-foreground">
              MSRP total: <strong className="text-white">${msrp.toFixed(2)}</strong>
              {b.bundle_price && <> · Bundle price: <strong className="text-primary">${parseFloat(b.bundle_price).toFixed(2)}</strong> · Save <strong className="text-primary">${(msrp - parseFloat(b.bundle_price)).toFixed(2)}</strong></>}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="border-white/15">Cancel</Button>
          <Button className="btn-primary" onClick={save} disabled={saving} data-testid="bundle-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function BroadcastTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    subject: "NovalynxTech is live — premium electronics, honest prices",
    headline: "We're live. Come see what's inside.",
    body: "Curated audio, wearables, and smart-home tech — fast worldwide shipping and honest prices.\nNew arrivals just landed. Grab something you'll actually keep.",
    cta_label: "Shop now",
    cta_url: "",
    audience: "all",
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const { data: subs = { count: 0, items: [] } } = useQuery({
    queryKey: ["adminSubscribers"],
    queryFn: adminApi.listSubscribers,
  });

  // For the audience dropdown labels
  const everyoneCounts = {
    users: "…", customers: "…", admins: "…",
    subscribers: subs.count,
  };

  const send = async (test_only) => {
    if (!form.subject.trim() || !form.headline.trim() || !form.body.trim()) {
      toast.error("Subject, headline and body are required"); return;
    }
    setSending(true); setResult(null);
    try {
      const r = await adminApi.broadcast({ ...form, test_only });
      setResult(r);
      toast.success(test_only ? "Test email sent to your inbox" : `Broadcast sent to ${r.sent} of ${r.recipients} recipients`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    }
    setSending(false);
  };

  const removeSub = async (email) => {
    if (!confirm(`Remove ${email} from the subscriber list?`)) return;
    try {
      await adminApi.removeSubscriber(email);
      qc.invalidateQueries({ queryKey: ["adminSubscribers"] });
      toast.success("Subscriber removed");
    } catch (e) { toast.error("Failed to remove"); }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="admin-broadcast">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Send announcement</h2>
        <p className="text-sm text-muted-foreground">Draft a launch, sale or product-drop email and blast it to your users. Preview shows a black + yellow branded template.</p>
      </div>

      {/* Subscriber list */}
      <div className="rounded-2xl bg-card border border-white/5 overflow-hidden" data-testid="subscriber-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div>
            <p className="text-sm font-semibold">Email subscribers</p>
            <p className="text-xs text-muted-foreground">People who claimed the WELCOME10 exit-intent code.</p>
          </div>
          <div className="text-right">
            <p className="font-display text-3xl font-bold text-primary leading-none" data-testid="subscriber-count">{subs.count}</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">on the list</p>
          </div>
        </div>
        {subs.count === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground">
            No subscribers yet. As soon as someone claims the exit-intent 10% off code, they'll show up here — then you can target them with the "Email subscribers only" audience below.
          </div>
        ) : (
          <div className="max-h-56 overflow-y-auto divide-y divide-white/5">
            {subs.items.slice(0, 200).map((s) => (
              <div key={s.email} className="flex items-center justify-between px-5 py-2 text-sm" data-testid={`sub-${s.email}`}>
                <div className="min-w-0">
                  <p className="truncate">{s.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.source || "manual"} · {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                  </p>
                </div>
                <button onClick={() => removeSub(s.email)} className="text-xs text-muted-foreground hover:text-destructive transition" data-testid={`remove-sub-${s.email}`}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 rounded-2xl bg-card border border-white/5 p-5">
        <div>
          <Label>Subject line</Label>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="broadcast-subject" />
        </div>
        <div>
          <Label>Headline (shown large inside the email)</Label>
          <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} data-testid="broadcast-headline" />
        </div>
        <div>
          <Label>Body (blank lines = new paragraph)</Label>
          <Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="broadcast-body" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Button label</Label>
            <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="broadcast-cta-label" />
          </div>
          <div>
            <Label>Button URL (leave blank for /shop)</Label>
            <Input placeholder="https://…" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} data-testid="broadcast-cta-url" />
          </div>
        </div>
        <div>
          <Label>Audience</Label>
          <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
            <SelectTrigger data-testid="broadcast-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users ({everyoneCounts.users})</SelectItem>
              <SelectItem value="customer">Customers only ({everyoneCounts.customers})</SelectItem>
              <SelectItem value="admin">Admins only ({everyoneCounts.admins})</SelectItem>
              <SelectItem value="subscribers">Email subscribers only ({everyoneCounts.subscribers})</SelectItem>
              <SelectItem value="everyone">Everyone (users + subscribers)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <div className="px-4 py-2 text-xs text-muted-foreground uppercase tracking-widest bg-secondary/40">Preview</div>
        <div className="bg-[#0a0a0a] p-6 text-white">
          <span className="inline-block px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest">NovalynxTech</span>
          <h3 className="font-display text-3xl font-bold mt-4 mb-3">{form.headline || "Your headline"}</h3>
          <div className="text-sm text-muted-foreground space-y-3">
            {(form.body || "").split("\n").filter((p) => p.trim()).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="mt-5">
            <span className="inline-block bg-primary text-primary-foreground font-bold px-5 py-2.5 rounded-lg text-sm">{form.cta_label || "Shop now"}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" className="border-white/15" onClick={() => send(true)} disabled={sending} data-testid="broadcast-send-test">
          {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : "Send test to me"}
        </Button>
        <Button className="btn-primary" onClick={() => { if (confirm(`Send to all ${form.audience === "all" ? "users" : form.audience}s? This cannot be undone.`)) send(false); }} disabled={sending} data-testid="broadcast-send-all">
          {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : "Send to audience"}
        </Button>
        {result && (
          <p className="text-sm text-muted-foreground self-center" data-testid="broadcast-result">
            {result.test_only ? "Test sent." : `Delivered to ${result.sent}/${result.recipients} recipients.`}
          </p>
        )}
      </div>
    </div>
  );
}


function SettingsTab() {
  return (
    <div className="max-w-3xl space-y-10" data-testid="admin-settings">
      <AnnouncementEditor />
      <CountdownEditor />
      <CsvImporter />
    </div>
  );
}

function CountdownEditor() {
  const [state, setState] = useState({ active: false, message: "Launch sale ends in", end_at: "", cta_label: "", cta_url: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    adminApi.getCountdown().then((d) => {
      if (d?.active) setState({
        active: true,
        message: d.message || "Launch sale ends in",
        end_at: d.end_at ? new Date(d.end_at).toISOString().slice(0, 16) : "",
        cta_label: d.cta_label || "",
        cta_url: d.cta_url || "",
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Convert local datetime-local input back to ISO UTC before sending
      const iso = state.end_at ? new Date(state.end_at).toISOString() : "";
      await adminApi.saveCountdown({ ...state, end_at: iso });
      toast.success(state.active && iso ? "Countdown is live" : "Countdown hidden");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const setPreset = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(23, 59, 0, 0);
    setState({ ...state, active: true, end_at: d.toISOString().slice(0, 16) });
  };

  if (loading) return null;
  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Launch countdown banner</h2>
      <p className="text-sm text-muted-foreground mb-4">A live-ticking countdown that sits below the announcement bar. Perfect for launch sales, flash promos, or Black Friday urgency. Auto-hides when the timer expires.</p>
      <div className="rounded-2xl bg-card border border-white/10 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Switch checked={state.active} onCheckedChange={(v) => setState({ ...state, active: v })} data-testid="countdown-active" />
          <Label>Show on site</Label>
        </div>
        <div>
          <Label>Message</Label>
          <Input value={state.message} onChange={(e) => setState({ ...state, message: e.target.value })} placeholder="Launch sale ends in" data-testid="countdown-message" />
        </div>
        <div>
          <Label>End date & time (your local time)</Label>
          <div className="flex gap-2 items-center">
            <Input type="datetime-local" value={state.end_at} onChange={(e) => setState({ ...state, end_at: e.target.value })} data-testid="countdown-end" />
            <Button type="button" variant="outline" className="border-white/15" onClick={() => setPreset(1)}>+1d</Button>
            <Button type="button" variant="outline" className="border-white/15" onClick={() => setPreset(3)}>+3d</Button>
            <Button type="button" variant="outline" className="border-white/15" onClick={() => setPreset(7)}>+7d</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>CTA label (optional)</Label>
            <Input value={state.cta_label} onChange={(e) => setState({ ...state, cta_label: e.target.value })} placeholder="Shop the sale" data-testid="countdown-cta-label" />
          </div>
          <div>
            <Label>CTA link (optional)</Label>
            <Input value={state.cta_url} onChange={(e) => setState({ ...state, cta_url: e.target.value })} placeholder="/shop?sort=discount" data-testid="countdown-cta-url" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button className="btn-primary" onClick={save} disabled={saving} data-testid="countdown-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : "Save countdown"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AnnouncementEditor() {
  const [state, setState] = useState({ text: "", link_label: "", link_url: "", active: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    adminApi.getAnnouncement().then((d) => {
      if (d?.active) setState({ text: d.text || "", link_label: d.link_label || "", link_url: d.link_url || "", active: true });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveAnnouncement(state);
      toast.success(state.active && state.text.trim() ? "Announcement live" : "Announcement hidden");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Announcement bar</h2>
      <p className="text-sm text-muted-foreground mb-4">A slim yellow strip that sits above the header on every page. Great for launch promos, free-shipping thresholds or coupon codes. Users can dismiss it once per version.</p>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
        <div className="grid gap-4 rounded-2xl bg-card border border-white/5 p-5">
          <div>
            <Label>Text (max 180 chars)</Label>
            <Input value={state.text} maxLength={180} placeholder="Launch sale — code NOVA20 for 20% off"
              onChange={(e) => setState({ ...state, text: e.target.value })} data-testid="ann-text" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Link label (optional)</Label>
              <Input value={state.link_label} placeholder="Shop the sale"
                onChange={(e) => setState({ ...state, link_label: e.target.value })} data-testid="ann-link-label" />
            </div>
            <div>
              <Label>Link URL (optional)</Label>
              <Input value={state.link_url} placeholder="/shop or https://…"
                onChange={(e) => setState({ ...state, link_url: e.target.value })} data-testid="ann-link-url" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={state.active} onCheckedChange={(v) => setState({ ...state, active: v })} data-testid="ann-active" />
            <Label>Active (show on site)</Label>
          </div>

          {state.text.trim() && (
            <div className="rounded-lg overflow-hidden border border-white/10">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary/40">Live preview</div>
              <div className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium flex items-center gap-3">
                <span className="flex-1">{state.text}{state.link_url && state.link_label && <a className="ml-2 underline font-bold">{state.link_label} →</a>}</span>
                <span className="opacity-70">×</span>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button className="btn-primary" onClick={save} disabled={saving} data-testid="ann-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : "Save announcement"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CsvImporter() {
  const qc = useQueryClient();
  const [csv, setCsv] = useState("title,price,image,category,brand,description\nExample Earbuds Pro,49.99,https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=800,earbuds,AudioPro,Crystal-clear sound with ANC.");
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!csv.trim()) { toast.error("Paste some CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await adminApi.csvImport({ csv_text: csv, publish });
      setResult(r);
      toast.success(`Imported ${r.imported}${r.failed ? ` · failed ${r.failed}` : ""}`);
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
    } catch (e) { toast.error(e.response?.data?.detail || "CSV import failed"); }
    setBusy(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    setCsv(text);
    toast.info(`Loaded ${file.name}`);
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">CSV product import</h2>
      <p className="text-sm text-muted-foreground mb-4">Paste rows or upload a CSV file. Required columns: <code className="text-primary">title</code>, <code className="text-primary">price</code>. Optional: <code>image</code>, <code>category</code>, <code>brand</code>, <code>description</code>, <code>compare_at_price</code>, <code>inventory</code>. Header is row 1.</p>

      <div className="grid gap-4 rounded-2xl bg-card border border-white/5 p-5">
        <div className="flex items-center gap-3">
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="csv-file-input"
            className="text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-secondary file:text-white file:text-xs file:cursor-pointer" />
          <div className="flex items-center gap-2 ml-auto">
            <Switch checked={publish} onCheckedChange={setPublish} data-testid="csv-publish" />
            <Label className="text-sm">Publish immediately</Label>
          </div>
        </div>
        <div>
          <Label>CSV rows</Label>
          <Textarea rows={9} value={csv} onChange={(e) => setCsv(e.target.value)} className="font-mono text-xs" data-testid="csv-text" />
        </div>
        <div className="flex justify-end">
          <Button className="btn-primary" onClick={submit} disabled={busy} data-testid="csv-import-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : "Import CSV"}
          </Button>
        </div>
        {result && (
          <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-xs" data-testid="csv-result">
            <p className="mb-2 text-muted-foreground">Imported <strong className="text-primary">{result.imported}</strong> · Failed <strong className={result.failed ? "text-destructive" : ""}>{result.failed}</strong></p>
            {result.failed > 0 && (
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {result.results.filter((r) => r.status === "failed").map((r, i) => (
                  <li key={i} className="text-muted-foreground">Row {r.row}: {r.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

